'use client';

import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import IconButton from '@savvy-kit/components/icon-button';
import { cn } from '@savvy-kit/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';

type HorizontalSliderProps = {
  readonly children: ReactNode;
  readonly scrollAmount?: number;
  readonly dependencies?: unknown[];
  /**
   * Merged onto the slider's root flex row. Useful for overriding the default
   * `gap-2` between the chevrons and the scrollable content — e.g. canvas tabs
   * want the chevrons sitting flush against the tab list.
   */
  readonly className?: string;
};

function HorizontalSlider({ children, scrollAmount = 200, dependencies = [], className }: HorizontalSliderProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollButtons = useCallback(() => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const hasScroll = scrollWidth > clientWidth;
      setHasOverflow(hasScroll);
      if (hasScroll) {
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
      } else {
        setCanScrollLeft(false);
        setCanScrollRight(false);
      }
    }
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const currentScrollLeft = scrollRef.current.scrollLeft;
      const newScrollLeft = direction === 'left' ? currentScrollLeft - scrollAmount : currentScrollLeft + scrollAmount;
      scrollRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
      setTimeout(checkScrollButtons, 100);
    }
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    checkScrollButtons();

    scrollElement.addEventListener('scroll', checkScrollButtons);
    const resizeObserver = new ResizeObserver(checkScrollButtons);
    resizeObserver.observe(scrollElement);

    return () => {
      scrollElement.removeEventListener('scroll', checkScrollButtons);
      resizeObserver.disconnect();
    };
  }, [checkScrollButtons, ...dependencies]);

  return (
    <div className={cn('flex items-center gap-2 w-full min-w-0', hasOverflow && '-mx-3', className)}>
      {hasOverflow && (
        <IconButton
          size="sm"
          variant="ghost"
          onClick={() => scroll('left')}
          className="flex-shrink-0"
          disabled={!canScrollLeft}
        >
          <ChevronLeft />
        </IconButton>
      )}
      <div
        ref={scrollRef}
        className="flex-1 min-w-0 overflow-x-auto [&::-webkit-scrollbar]:hidden"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {children}
      </div>
      {hasOverflow && (
        <IconButton
          size="sm"
          variant="ghost"
          onClick={() => scroll('right')}
          className="flex-shrink-0"
          disabled={!canScrollRight}
        >
          <ChevronRight />
        </IconButton>
      )}
    </div>
  );
}

export default HorizontalSlider;
